import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full rounded-xl border border-navy/12 bg-white px-4 py-3 text-sm text-navy placeholder:text-muted/70 outline-none transition-[border,box-shadow] duration-300 focus:border-gold focus:ring-2 focus:ring-gold/25";

export const LeadForm = ({ interest = "", dark = false }) => {
  const { t, c } = useI18n();
  const f = t.form;
  const [form, setForm] = useState({ name: "", email: "", phone: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error(f.errRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, interest });
      setDone(true);
      toast.success(f.successToast);
      setForm({ name: "", email: "", phone: "", budget: "", message: "" });
    } catch (err) {
      toast.error(f.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  const label = dark ? "text-white/70" : "text-navy/70";

  if (done) {
    return (
      <div className="flex flex-col items-center justify-center rounded-3xl bg-white p-10 text-center shadow-soft" data-testid="lead-form-success">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold"><CheckCircle2 size={34} /></span>
        <h3 className="mt-5 font-display text-2xl font-semibold text-navy">{f.successTitle}</h3>
        <p className="mt-2 max-w-sm text-sm text-muted">{f.successBody}</p>
        <button onClick={() => setDone(false)} data-testid="lead-form-reset" className="btn-ghost mt-6">{f.another}</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className="rounded-3xl bg-white p-6 shadow-soft sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={`mb-1.5 block text-xs font-medium ${label}`}>{f.name} *</label>
          <input data-testid="lead-name" className={field} value={form.name} onChange={set("name")} placeholder={f.namePh} />
        </div>
        <div>
          <label className={`mb-1.5 block text-xs font-medium ${label}`}>{f.phone} *</label>
          <input data-testid="lead-phone" className={field} value={form.phone} onChange={set("phone")} placeholder={f.phonePh} />
        </div>
        <div>
          <label className={`mb-1.5 block text-xs font-medium ${label}`}>{f.email} *</label>
          <input data-testid="lead-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder={f.emailPh} />
        </div>
        <div>
          <label className={`mb-1.5 block text-xs font-medium ${label}`}>{f.budget}</label>
          <select data-testid="lead-budget" className={field} value={form.budget} onChange={set("budget")}>
            <option value="">{f.selectBudget}</option>
            {c.BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-4">
        <label className={`mb-1.5 block text-xs font-medium ${label}`}>{f.message}</label>
        <textarea data-testid="lead-message" rows={4} className={`${field} resize-none`} value={form.message} onChange={set("message")}
          placeholder={interest ? `${f.msgPhInterest} ${interest}...` : f.msgPh} />
      </div>
      {interest && <p className="mt-3 text-xs text-muted">{f.enquiryAbout} <span className="font-medium text-navy">{interest}</span></p>}
      <button type="submit" disabled={loading} data-testid="lead-submit" className="btn-primary mt-6 w-full disabled:opacity-60">
        {loading ? <><Loader2 size={17} className="animate-spin" /> {f.sending}</> : <>{f.submit} <Send size={16} /></>}
      </button>
      <p className="mt-3 text-center text-xs text-muted">{f.note}</p>
    </form>
  );
};
